// =============================================================================
// FORM BUILDER STORE (ZUSTAND) - Professional form builder
// =============================================================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Form, FormField, FormSection, FormSettings, FormTheme, FieldType, ValidationRule, ConditionalLogic } from './types';
import { FIELD_TEMPLATES, DEFAULT_THEME } from './types';

interface FormBuilderState {
  // Form state
  form: Form | null;
  sections: FormSection[];
  settings: FormSettings;
  theme: FormTheme;

  // UI state
  selectedFieldId: string | null;
  selectedSectionId: string | null;
  previewMode: boolean;
  isDragging: boolean;
  activeTab: 'fields' | 'settings' | 'theme' | 'logic';
  searchQuery: string;

  // Actions
  setForm: (form: Form | null) => void;
  updateFormName: (name: string) => void;
  updateFormDescription: (description: string) => void;

  // Section actions
  addSection: (afterSectionId?: string) => FormSection;
  updateSection: (sectionId: string, updates: Partial<FormSection>) => void;
  removeSection: (sectionId: string) => void;
  reorderSections: (fromIndex: number, toIndex: number) => void;

  // Field actions
  addField: (sectionId: string, fieldType: FieldType, afterFieldId?: string) => FormField;
  updateField: (sectionId: string, fieldId: string, updates: Partial<FormField>) => void;
  removeField: (sectionId: string, fieldId: string) => void;
  duplicateField: (sectionId: string, fieldId: string) => void;
  moveField: (
    fromSectionId: string,
    toSectionId: string,
    fromIndex: number,
    toIndex: number
  ) => void;

  // Validation actions
  addValidation: (sectionId: string, fieldId: string, rule: ValidationRule) => void;
  removeValidation: (sectionId: string, fieldId: string, ruleIndex: number) => void;

  // Logic actions
  addCondition: (sectionId: string, fieldId: string, condition: ConditionalLogic) => void;
  removeCondition: (sectionId: string, fieldId: string, conditionIndex: number) => void;

  // Settings & Theme actions
  updateSettings: (updates: Partial<FormSettings>) => void;
  updateTheme: (updates: Partial<FormTheme>) => void;

  // Selection
  selectField: (fieldId: string | null) => void;
  selectSection: (sectionId: string | null) => void;

  // UI actions
  togglePreview: () => void;
  setIsDragging: (isDragging: boolean) => void;
  setActiveTab: (tab: 'fields' | 'settings' | 'theme' | 'logic') => void;
  setSearchQuery: (query: string) => void;

  // Persistence
  exportForm: () => Form;
  importForm: (form: Form) => void;
  reset: () => void;
}

const generateId = () => crypto.randomUUID();

const DEFAULT_SETTINGS: FormSettings = {
  submitLabel: 'Envoyer',
  resetLabel: 'Réinitialiser',
  successMessage: 'Merci pour votre soumission !',
  allowMultipleSubmissions: false,
  showProgressBar: false,
  showPageNumbers: false,
  showRequiredAsterisk: true,
  layout: 'default',
  labelPosition: 'top',
};

export const useFormBuilderStore = create<FormBuilderState>()(
  immer((set, get) => ({
    // Initial state
    form: null,
    sections: [
      {
        id: generateId(),
        title: 'Section 1',
        fields: [],
      },
    ],
    settings: DEFAULT_SETTINGS,
    theme: DEFAULT_THEME,
    selectedFieldId: null,
    selectedSectionId: null,
    previewMode: false,
    isDragging: false,
    activeTab: 'fields',
    searchQuery: '',

    // Form actions
    setForm: (form) =>
      set((state) => {
        state.form = form;
        if (form) {
          state.sections = form.sections;
          state.settings = form.settings;
          state.theme = form.theme || DEFAULT_THEME;
        } else {
          state.sections = [{ id: generateId(), title: 'Section 1', fields: [] }];
          state.settings = DEFAULT_SETTINGS;
          state.theme = DEFAULT_THEME;
        }
      }),

    updateFormName: (name) =>
      set((state) => {
        if (state.form) {
          state.form.name = name;
        }
      }),

    updateFormDescription: (description) =>
      set((state) => {
        if (state.form) {
          state.form.description = description;
        }
      }),

    // Section actions
    addSection: (afterSectionId) => {
      const newSection: FormSection = {
        id: generateId(),
        title: `Section ${get().sections.length + 1}`,
        fields: [],
      };

      set((state) => {
        if (afterSectionId) {
          const index = state.sections.findIndex((s) => s.id === afterSectionId);
          state.sections.splice(index + 1, 0, newSection);
        } else {
          state.sections.push(newSection);
        }
      });

      return newSection;
    },

    updateSection: (sectionId, updates) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          Object.assign(section, updates);
        }
      }),

    removeSection: (sectionId) =>
      set((state) => {
        if (state.sections.length > 1) {
          state.sections = state.sections.filter((s) => s.id !== sectionId);
          if (state.selectedSectionId === sectionId) {
            state.selectedSectionId = null;
          }
        }
      }),

    reorderSections: (fromIndex, toIndex) =>
      set((state) => {
        const item = state.sections[fromIndex];
        if (item) {
          const [removed] = state.sections.splice(fromIndex, 1);
          if (removed) state.sections.splice(toIndex, 0, removed);
        }
      }),

    // Field actions
    addField: (sectionId, fieldType, afterFieldId) => {
      const template = FIELD_TEMPLATES.find((t) => t.type === fieldType);

      const newField: FormField = {
        id: generateId(),
        type: fieldType,
        name: `field_${Date.now()}`,
        label: template?.labelFr || template?.label || 'Nouveau champ',
        validation: [],
        width: 'full',
        ...template?.defaultConfig,
      };

      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          if (afterFieldId) {
            const index = section.fields.findIndex((f) => f.id === afterFieldId);
            section.fields.splice(index + 1, 0, newField);
          } else {
            section.fields.push(newField);
          }
          state.selectedFieldId = newField.id;
        }
      });

      return newField;
    },

    updateField: (sectionId, fieldId, updates) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            Object.assign(field, updates);
          }
        }
      }),

    removeField: (sectionId, fieldId) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          section.fields = section.fields.filter((f) => f.id !== fieldId);
          if (state.selectedFieldId === fieldId) {
            state.selectedFieldId = null;
          }
        }
      }),

    duplicateField: (sectionId, fieldId) => {
      const section = get().sections.find((s) => s.id === sectionId);
      const field = section?.fields.find((f) => f.id === fieldId);

      if (!field) return;

      const newField: FormField = {
        ...field,
        id: generateId(),
        name: `${field.name}_copy`,
        label: `${field.label} (copie)`,
      };

      set((state) => {
        const sec = state.sections.find((s) => s.id === sectionId);
        if (sec) {
          const index = sec.fields.findIndex((f) => f.id === fieldId);
          sec.fields.splice(index + 1, 0, newField);
          state.selectedFieldId = newField.id;
        }
      });
    },

    moveField: (fromSectionId, toSectionId, fromIndex, toIndex) =>
      set((state) => {
        const fromSection = state.sections.find((s) => s.id === fromSectionId);
        const toSection = state.sections.find((s) => s.id === toSectionId);

        if (fromSection && toSection && fromSection.fields[fromIndex]) {
          const [removed] = fromSection.fields.splice(fromIndex, 1);
          if (removed) toSection.fields.splice(toIndex, 0, removed);
        }
      }),

    // Validation actions
    addValidation: (sectionId, fieldId, rule) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            field.validation.push(rule);
          }
        }
      }),

    removeValidation: (sectionId, fieldId, ruleIndex) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            field.validation.splice(ruleIndex, 1);
          }
        }
      }),

    // Logic actions
    addCondition: (sectionId, fieldId, condition) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field) {
            if (!field.conditions) field.conditions = [];
            field.conditions.push(condition);
          }
        }
      }),

    removeCondition: (sectionId, fieldId, conditionIndex) =>
      set((state) => {
        const section = state.sections.find((s) => s.id === sectionId);
        if (section) {
          const field = section.fields.find((f) => f.id === fieldId);
          if (field && field.conditions) {
            field.conditions.splice(conditionIndex, 1);
          }
        }
      }),

    // Settings & Theme actions
    updateSettings: (updates) =>
      set((state) => {
        Object.assign(state.settings, updates);
      }),

    updateTheme: (updates) =>
      set((state) => {
        Object.assign(state.theme, updates);
      }),

    // Selection
    selectField: (fieldId) =>
      set((state) => {
        state.selectedFieldId = fieldId;
      }),

    selectSection: (sectionId) =>
      set((state) => {
        state.selectedSectionId = sectionId;
      }),

    // UI actions
    togglePreview: () =>
      set((state) => {
        state.previewMode = !state.previewMode;
        state.selectedFieldId = null;
      }),

    setIsDragging: (isDragging) =>
      set((state) => {
        state.isDragging = isDragging;
      }),

    setActiveTab: (tab) =>
      set((state) => {
        state.activeTab = tab;
      }),

    setSearchQuery: (query) =>
      set((state) => {
        state.searchQuery = query;
      }),

    // Persistence
    exportForm: () => {
      const state = get();
      return {
        id: state.form?.id || generateId(),
        name: state.form?.name || 'Formulaire sans titre',
        description: state.form?.description,
        sections: state.sections,
        settings: state.settings,
        theme: state.theme,
        createdAt: state.form?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },

    importForm: (form) =>
      set((state) => {
        state.form = form;
        state.sections = form.sections;
        state.settings = form.settings;
        state.theme = form.theme || DEFAULT_THEME;
      }),

    reset: () =>
      set((state) => {
        state.form = null;
        state.sections = [{ id: generateId(), title: 'Section 1', fields: [] }];
        state.settings = DEFAULT_SETTINGS;
        state.theme = DEFAULT_THEME;
        state.selectedFieldId = null;
        state.selectedSectionId = null;
        state.previewMode = false;
        state.activeTab = 'fields';
        state.searchQuery = '';
      }),
  }))
);
